// src/frontend/Adviser/AgencyCodes/GetAdviserAgencyCodes.jsx
import { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../../../DesignSystem/Sidebar";
import InputField from "../../../DesignSystem/InputField";
import "../../../../css/Adviser/Adviser.css";
import { useAdviserContext } from "../../../../context/Adviser/AdviserContext";
import { useAdviserActions } from "../../../../context/Adviser/Hooks/useAdviserActions";
import ButtonYellow from "../../../DesignSystem/Buttons/ButtonYellow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import ButtonGreen from "../../../DesignSystem/Buttons/ButtonGreen";
import {
  agencyCodeProviders,
  agencyCodeProvidersFP,
} from "../../../RecruitmentData/HelperFunctions/AgencyCodeProviders";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";
import { fetchWithFallback } from "../../../../utils/fetchWithFallback";
import { formatDate } from "../../../../context/Adviser/Utils/FormatDate";
import { saveAdviserData } from "../../../../context/Adviser/SaveAdviser";
import { useFormUtils } from "../../../../context/Adviser/Utils/FormUtils";

const GetAdviserAgencyCodes = () => {
  const { docId } = useParams();
  const [isComplete, setIsComplete] = useState(true);
  const [emailPreview, setEmailPreview] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isFinancialPlanner, setIsFinancialPlanner] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const emailRef = useRef(null);
  const { gv, hc, hcr } = useFormUtils();

  const { state, dispatch } = useAdviserContext();
  const { loadAdviser } = useAdviserActions();

  // pull from the new shape
  const data = state?.data || {};
  const baseData = data.baseData || {};
  const personalDetails = data.personalDetails || {};
  const applicationDetails = data.applicationDetails || {};
  const agencyCodes = data.agencyCodes || {}; // may be absent if not fetched
  const ARDetails = data.ARDetails || {};
  const letterForAgenciesEnv = process.env.REACT_APP_LETTER_FOR_AGENCIES_2025;

  useEffect(() => {
    loadAdviser(docId
      // , {
      // forceRefresh: true,
      // useLS: true,
      // }
    );
    // console.log("CTX state:", state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  // derive flags & recipients from application details
  useEffect(() => {
    const businessAreasRaw = gv(applicationDetails?.businessAreas);
    const applyingAsRaw = gv(applicationDetails?.applyingAs);

    const businessAreasArray = Array.isArray(businessAreasRaw)
      ? businessAreasRaw
      : typeof businessAreasRaw === "string"
      ? businessAreasRaw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    const isFP =
      businessAreasArray.includes("Financial Planning") ||
      applyingAsRaw === "Financial Planner";

    setIsFinancialPlanner(!!isFP);
    setEmailRecipients(
      isFP
        ? [...agencyCodeProviders, ...agencyCodeProvidersFP]
        : [...agencyCodeProviders]
    );
  }, [applicationDetails?.businessAreas, applicationDetails?.applyingAs]);

  // contentEditable handler
  const handleEmailInput = (e) => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(emailRef.current);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    const preCaretPosition = preCaretRange.toString().length;

    setEmailPreview(e.currentTarget.innerHTML);

    requestAnimationFrame(() => {
      if (!emailRef.current) return;
      const newRange = document.createRange();
      const sel = window.getSelection();
      let charCount = 0;
      let found = false;

      const restoreCursor = (node) => {
        if (found || !node) return;
        if (node.nodeType === Node.TEXT_NODE) {
          const next = charCount + node.textContent.length;
          if (preCaretPosition <= next) {
            newRange.setStart(node, preCaretPosition - charCount);
            found = true;
          }
          charCount = next;
        } else {
          node.childNodes.forEach(restoreCursor);
        }
      };

      restoreCursor(emailRef.current);
      if (found) {
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
    });
  };

  const handleSendEmails = () => {
    const emailBody = generateEmailPreview();
    setEmailPreview(emailBody);
    setShowModal(true);
  };

  const generateEmailPreview = () => {
    const timestamp = new Date();
    const greeting = timestamp.getHours() >= 12 ? "afternoon" : "morning";

    const forename = gv(personalDetails?.forename) || "";
    const surname = gv(personalDetails?.surname) || "";
    const applyingAs = gv(applicationDetails?.applyingAs) || "";
    const tradingStyle = gv(applicationDetails?.tradingStyle) || "";
    const applyingToJoinAs =
      gv(
        applicationDetails?.applyingToJoinAs ||
          applicationDetails?.applyingToJoin
      ) || "";

    const IN = gv(personalDetails?.individualReferenceNumber) || "";
    const fca = gv(personalDetails?.FCANumber) || "";
    const arFca = gv(ARDetails?.ARFCANumber) || "";

    const dob = formatDate(personalDetails?.dob);

    const mobile = gv(personalDetails?.mobileTelephone) || "";
    const arMobile = gv(applicationDetails?.ARMobileTelephone) || "";

    const address = [
      gv(personalDetails?.currentAddress),
      gv(personalDetails?.currentCounty),
      gv(personalDetails?.currentTown),
      gv(personalDetails?.currentPostcode),
    ]
      .filter(Boolean)
      .join(", ");

    const arAddress = gv(ARDetails?.ARaddress) || "";

    const commissionAddress =
      gv(personalDetails?.commissionStatementAddress) ||
      "Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU";
    const arCommissionAddress =
      gv(ARDetails?.ARcommissionStatementAddress) ||
      "Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU";

    const businessAreas = gv(applicationDetails?.businessAreas) || "";
    const arBusinessAreas = gv(applicationDetails?.ARBusinessAreas) || "";

    const subAgencyRequired = gv(personalDetails?.subAgencyRequired) || "";
    const arSubAgencyRequired = gv(ARDetails?.ARsubAgencyRequired) || "";

    return `
<!DOCTYPE html>
<html>
<head>
  <style>
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color:#081e56; color: white; }
  </style>
</head>
<body>
  <p>Good ${greeting} team,</p>
  <p>I hope you’re well.</p>
  <p>Please find attached a letter from the Director of Rosemount Financial Solutions (IFA) Ltd, requesting that a new sub-agency be created for
  <strong>${forename} ${surname}</strong>.</p>

  <p>${forename} is joining as a <strong>${applyingAs}</strong> adviser, under <strong>${tradingStyle}</strong>,
  the <strong>${applyingToJoinAs}</strong> of Rosemount Financial Solutions (IFA) Ltd.</p>

  <p>Adviser & AR Details:</p>
  <table border="1" cellspacing="0" cellpadding="5">
    <tr>
      <th></th>
      <th>Adviser</th>
      <th>AR</th>
    </tr>
    <tr>
      <td>Individual Reference Number (IN)</td>
      <td>${IN}</td>
      <td></td>
    </tr>
    <tr>
      <td>AR/TS Name</td>
      <td></td>
      <td>${tradingStyle}</td>
    </tr>
    <tr>
      <td>Date of Birth</td>
      <td>${dob}</td>
      <td></td>
    </tr>
    <tr>
      <td>FCA Number</td>
      <td>${fca}</td>
      <td>${arFca}</td>
    </tr>
    <tr>
      <td>Telephone Number</td>
      <td>${mobile}</td>
      <td>${arMobile}</td>
    </tr>
    <tr>
      <td>Correspondence Address</td>
      <td>${address}</td>
      <td>${arAddress}</td>
    </tr>
    <tr>
      <td>Commission Statement Address</td>
      <td>${commissionAddress}</td>
      <td>${arCommissionAddress}</td>
    </tr>
    <tr>
      <td>Business Types (Inv, Pens, Prot)</td>
      <td>${businessAreas}</td>
      <td>${arBusinessAreas}</td>
    </tr>
    <tr>
      <td>Sub Agency Required?</td>
      <td>${subAgencyRequired}</td>
      <td>${arSubAgencyRequired}</td>
    </tr>
  </table>

  <p>If there is anything missing, or you have any questions, please let me know.</p>
  <p>Thank you for your assistance.</p>

  <p>Kind Regards,</p>
  <p>The Recruitment Team</p>
</body>
</html>`;
  };

  const sendEmail = async () => {
    try {
      const letterForAgenciesUrl =
        gv(agencyCodes?.letterForAgencies) || letterForAgenciesEnv || "";

      let sevenIMBase64 = null;
      if (isFinancialPlanner) {
        sevenIMBase64 =
          gv(applicationDetails?.sevenIMAgencyCodeRequestForm) ||
          gv(baseData?.sevenIMAgencyCodeRequestForm) ||
          null;
      }

      for (const provider of emailRecipients) {
        if (!provider.emailAddress) continue;

        const attachments = { letter: letterForAgenciesUrl };
        if (
          isFinancialPlanner &&
          provider.provider === "7IM" &&
          sevenIMBase64
        ) {
          attachments.sevenIM = sevenIMBase64;
        }

        const forename = gv(personalDetails?.forename) || "";
        const surname = gv(personalDetails?.surname) || "";

        const emailData = {
          emailRecipients: [
            {
              emailAddress: provider.emailAddress,
              provider: provider.provider,
            },
          ],
          emailBody: emailPreview,
          subject: `Sub Agency Request for ${forename} ${surname}`,
          attachments,
        };

        await fetchWithFallback("/requestAgencyCodes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(emailData),
        });
      }

      alert("All Emails Sent Successfully!");
    } catch (error) {
      // console.error("Request Failed:", error);
    }
  };

  const handleFileDelete = async () => {};
  const extractFilename = (filePath) => {
    if (!filePath) return "";
    const decoded = decodeURIComponent(filePath);
    return decoded.split("/").pop().split("?")[0];
  };

  // if (state.loading) return <div>Loading...</div>;
  // if (!data || Object.keys(data).length === 0)
  //   return <div>No data found for {docId}.</div>;

  // names for heading
  const hForename =
    gv(personalDetails?.forename) || gv(baseData?.forename) || "";
  const hSurname = gv(personalDetails?.surname) || gv(baseData?.surname) || "";

  return (
    <div className="Main-Page">
      <Sidebar />
      <div className="Main-Container">
        <div className="AdviserContainer">
          <h2>
            {hForename} {hSurname}
          </h2>

          <div className="AdviserDashboardColumn">
            <div className="SubTitle">Adviser Details</div>
            <div className="AdviserDashboardRow">
              <div className="AdviserDashboardColumn">
                <InputField
                  title="Forename"
                  value={gv(personalDetails?.forename) || ""}
                  onChange={hc("personalDetails", "forename")}
                />
                <InputField
                  title="Surname"
                  value={gv(personalDetails?.surname) || ""}
                  onChange={hc("personalDetails", "surname")}
                />
                <InputField
                  title="Email Address"
                  value={gv(personalDetails?.emailAddress) || ""}
                  onChange={hc("personalDetails", "emailAddress")}
                />
                <InputField
                  title="Date of Birth"
                  value={formatDate(personalDetails?.dob)}
                  onChange={() => {}}
                />
              </div>

              <div className="AdviserDashboardColumn">
                <InputField
                  title="Appointed Representative/ Trading Style Name"
                  value={gv(applicationDetails?.tradingStyle) || ""}
                  onChange={hc("applicationDetails", "tradingStyle")}
                />
                <InputField
                  title="Applying to Join as (Appointed Representative/ Trading Style)"
                  value={gv(applicationDetails?.applyingToJoin) || ""}
                  onChange={hc("applicationDetails", "applyingToJoin")}
                />
                <InputField
                  title="Applying as (Mortgage & Protection/ Financial Planning/ Protection only) Adviser"
                  value={gv(applicationDetails?.applyingAs) || ""}
                  isTextarea
                  onChange={hc("applicationDetails", "applyingAs")}
                />
              </div>
            </div>
          </div>

          <div className="AdviserDashboardColumn">
            <div className="SubTitle">Details to be sent to the Providers</div>
            <div className="AdviserDashboardRow">
              <div className="AdviserDashboardColumn">
                <div className="Content-SemiBold">Adviser Details</div>
                <InputField
                  title="Individual Reference Number (IN)"
                  value={gv(personalDetails?.individualReferenceNumber) || ""}
                  onChange={hc("personalDetails", "individualReferenceNumber")}
                />
                <InputField
                  title="FCA Number"
                  value={gv(personalDetails?.FCANumber) || ""}
                  onChange={hc("personalDetails", "FCANumber")}
                />
                <InputField
                  title="Telephone Number"
                  value={gv(personalDetails?.mobileTelephone) || ""}
                  onChange={hc("personalDetails", "mobileTelephone")}
                />
                <InputField
                  title="Correspondence Address"
                  value={[
                    gv(personalDetails?.currentAddress),
                    gv(personalDetails?.currentCounty),
                    gv(personalDetails?.currentTown),
                    gv(personalDetails?.currentPostcode),
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  isTextarea
                  onChange={() => {}}
                />
                <InputField
                  title="Commission Statement Address"
                  isTextarea
                  value={
                    gv(personalDetails?.commissionStatementAddress) ||
                    "Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU."
                  }
                  onChange={() => {}}
                />
                <InputField
                  title="Business Types (Inv, Pens, Prot)"
                  isTextarea
                  value={gv(applicationDetails?.businessAreas) || ""}
                  onChange={() => {}}
                />

                <div
                  className="AdviserDashboardRow"
                  style={{ alignItems: "center" }}
                >
                  <div className="Content">Sub Agency required?</div>
                  <div
                    className="AdviserDashboardSmallRow"
                    style={{ justifyContent: "flex-end", gap: "50px" }}
                  >
                    <label className="Radio-Option">
                      <input
                        type="radio"
                        name="subAgencyRequired"
                        value="Yes"
                        checked={
                          gv(personalDetails?.subAgencyRequired) === "Yes"
                        }
                        onChange={hcr(
                          "personalDetails",
                          "subAgencyRequired",
                          "Yes"
                        )}
                      />
                      <span className="Custom-RadioButton"></span>
                      <div className="Content">Yes</div>
                    </label>
                    <label className="Radio-Option">
                      <input
                        type="radio"
                        name="subAgencyRequired"
                        value="No"
                        checked={
                          gv(personalDetails?.subAgencyRequired) === "No"
                        }
                        onChange={hcr(
                          "personalDetails",
                          "subAgencyRequired",
                          "No"
                        )}
                      />
                      <span className="Custom-RadioButton"></span>
                      <div className="Content">No</div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="AdviserDashboardColumn">
                <div className="Content-SemiBold">AR Details</div>
                <InputField
                  title="AR Name"
                  value={gv(applicationDetails?.tradingStyle) || ""}
                  onChange={hc("applicationDetails", "tradingStyle")}
                />
                <InputField
                  title="FCA Number"
                  value={gv(ARDetails?.ARFCANumber) || ""}
                  onChange={hc("ARDetails", "ARFCANumber")}
                />
                <InputField
                  title="Telephone Number"
                  value={gv(applicationDetails?.ARMobileTelephone) || ""}
                  onChange={hc("applicationDetails", "ARMobileTelephone")}
                />
                <InputField
                  title="Correspondence Address"
                  value={gv(ARDetails?.ARaddress) || ""}
                  isTextarea
                  onChange={() => {}}
                />
                <InputField
                  title="Commission Statement Address"
                  value={
                    gv(ARDetails?.ARcommissionStatementAddress) ||
                    "Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU."
                  }
                  isTextarea
                  onChange={() => {}}
                />
                <InputField
                  title="Business Types (Inv, Pens, Prot)"
                  isTextarea
                  value={gv(applicationDetails?.ARBusinessAreas) || ""}
                  onChange={() => {}}
                />

                <div
                  className="AdviserDashboardRow"
                  style={{ alignItems: "center" }}
                >
                  <div className="Content">Sub Agency required?</div>
                  <div
                    className="AdviserDashboardSmallRow"
                    style={{ justifyContent: "flex-end", gap: "50px" }}
                  >
                    <label className="Radio-Option">
                      <input
                        type="radio"
                        name="ARsubAgencyRequired"
                        value="Yes"
                        checked={gv(ARDetails?.ARsubAgencyRequired) === "Yes"}
                        onChange={hcr(
                          "ARDetails",
                          "ARsubAgencyRequired",
                          "Yes"
                        )}
                      />
                      <span className="Custom-RadioButton"></span>
                      <div className="Content">Yes</div>
                    </label>
                    <label className="Radio-Option">
                      <input
                        type="radio"
                        name="ARsubAgencyRequired"
                        value="No"
                        checked={gv(ARDetails?.ARsubAgencyRequired) === "No"}
                        onChange={hcr("ARDetails", "ARsubAgencyRequired", "No")}
                      />
                      <span className="Custom-RadioButton"></span>
                      <div className="Content">No</div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="AdviserDashboardColumn">
            <div className="SubTitle">Attachments Required</div>
            <div className="AdviserDashboardColumn">
              {isFinancialPlanner && (
                <div className="AdviserDashboardRow">
                  <div className="Content">7IM: Platform user set up form</div>
                  {gv(applicationDetails?.sevenIMAgencyCodeRequestForm) ? (
                    <div className="AdviserDashboardSmallRow">
                      <a
                        href={gv(
                          applicationDetails?.sevenIMAgencyCodeRequestForm
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-link"
                      >
                        <div className="Content-SemiBold">
                          {extractFilename(
                            gv(applicationDetails?.sevenIMAgencyCodeRequestForm)
                          )}
                        </div>
                      </a>
                      <div className="DeleteIcon">
                        <FontAwesomeIcon icon={faTrash} onClick={() => {}} />
                      </div>
                    </div>
                  ) : (
                    <InputField
                      title="File"
                      specialType="file"
                      onChange={() => {}}
                    />
                  )}
                </div>
              )}

              <div className="AdviserDashboardRow">
                <div className="Content">Letter for Agencies 2025</div>
                <a
                  href={
                    gv(agencyCodes?.letterForAgencies) ||
                    letterForAgenciesEnv ||
                    "#"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-link"
                >
                  <div className="Content-SemiBold">
                    {extractFilename(
                      gv(agencyCodes?.letterForAgencies) || letterForAgenciesEnv
                    )}
                  </div>
                </a>
              </div>
            </div>
          </div>

          <div
            className="AdviserDashboardSmallRow"
            style={{ justifyContent: "center", gap: "50px" }}
          >
            <ButtonYellow
              buttonText="Save"
              onClick={() => saveAdviserData(docId, state.data)}
            />

            {isComplete && (
              <>
                <ButtonGreen
                  buttonText="Preview Email"
                  onClick={handleSendEmails}
                />
                <div>
                  {showModal && (
                    <div className="AdviserEmailPreviewPopupBackgroundContainer">
                      <div className="AdviserEmailPreviewPopup">
                        <div className="AdviserDashboardSmallRow">
                          <h2>Email Preview</h2>
                          <CloseIcon
                            className="CloseButton"
                            onClick={() => setShowModal(false)}
                          />
                        </div>
                        <div className="AdviserDashboardColumn">
                          <div
                            ref={emailRef}
                            contentEditable
                            className="EmailHolder"
                            onInput={handleEmailInput}
                            onBlur={(e) =>
                              setEmailPreview(e.currentTarget.innerHTML)
                            }
                            dangerouslySetInnerHTML={{ __html: emailPreview }}
                            suppressContentEditableWarning
                          />
                          <div className="AdviserDashboardSmallRow">
                            <ButtonYellow
                              onClick={() => setShowModal(false)}
                              buttonText="Cancel"
                            />
                            <ButtonGreen
                              onClick={sendEmail}
                              buttonText="Send Emails"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetAdviserAgencyCodes;
